import { charToken, isPositionalToken, tokenChar } from "@/domain/tokens";
import type { KeyboardLayout } from "@/domain/types";
import { charToCode, codeToChar, qwerty } from "@/hardware/keyboard-layouts/index";
import { rewriteValues } from "./index";

/**
 * The token a single key token serializes to in the target Keyboard Layout. The seed is
 * canonical US-Qwerty, so a char token resolves to its physical code through
 * qwerty; that code then emits the target's token. A positional token (Enter, F-keys,
 * mouse, numpad) is identical in every layout and passes through. The target's recovered
 * `tokens` map wins where present (national layouts bind OEM keys by virtual-key); else the
 * char path (charToken of the emitted char) reproduces the token for US-Latin layouts.
 */
function convertKey(token: string, layout: KeyboardLayout): string {
  if (isPositionalToken(token)) return token;
  // A digit token is virtual-key bound: SC2 keeps "1".."9","0" even on a layout whose number
  // row is shifted (AZERTY), so the digit names the number-row position, not a character.
  if (/^[0-9]$/.test(token)) return token;
  const ch = tokenChar(token);
  if (ch == null) return token; // a bare modifier or unknown token carries no position
  const code = charToCode(qwerty, ch);
  if (code == null) return token; // the seed names a char qwerty cannot place
  const fromTable = layout.tokens?.[code];
  if (fromTable != null) return fromTable;
  const targetChar = codeToChar(layout, code);
  return targetChar == null ? token : (charToken(targetChar) ?? token);
}

/** One combo's identity for the collision domain: modifier identity plus the key token. */
const comboOf = (modsKey: string, key: string): string => `${modsKey}|${key}`;

/**
 * Serialize the canonical US-Qwerty seed text into the .SC2Hotkeys file for a target Keyboard
 * Layout - the inverse of parse. The file is character-space: only the key
 * token of each binding moves; modifiers, the comma between alternatives, ordering, comments and
 * non-binding sections ([Settings]) are preserved exactly by the shared rewriteValues walker.
 *
 * ISO Enter collision (decision 0045): the tall ISO Enter absorbs the ANSI backslash position,
 * so the ISO layouts' tokens tables remap the US backslash key onto Enter and an incoming
 * BackSlash-derived [Hotkeys] bind can land on a combo a native Enter bind already holds. The
 * INCOMING bind yields, relocating to the layout's overflow key (chat is exercised constantly;
 * SC2's push-to-talk almost never - the frequently-used bind keeps its expected key). Scope:
 * [Hotkeys] global binds only; a [Commands] bind is card-scoped and coexists with chat in-game
 * (TheCore's own edition-6 UK file keeps both on Enter). If the overflow combo is itself
 * claimed, the incoming bind keeps Enter (fail open, the pre-0045 shape).
 */
export function convert(seedText: string, layout: KeyboardLayout): string {
  const overflow = layout.overflow;
  if (!overflow) return rewriteValues(seedText, (key) => convertKey(key, layout));
  // First pass: which combos the [Hotkeys] section claims natively on a contested token, and
  // which combos already land on an overflow target (displacing onto those would re-collide).
  // A DRY run of the same walker the rewrite uses, so both passes see the identical raw view
  // (same lines, same key slots, same section tracking) - a parsed view here would judge
  // whitespace and duplicate ids differently than the byte-faithful rewrite below.
  const native = new Set<string>();
  const occupied = new Set<string>();
  const targets = new Set(Object.values(overflow));
  rewriteValues(seedText, (rawKey, { section, modsKey }) => {
    if (section !== "hotkeys") return rawKey;
    const converted = convertKey(rawKey, layout);
    if (overflow[converted] != null && rawKey.trim() === converted)
      native.add(comboOf(modsKey, converted));
    if (targets.has(converted)) occupied.add(comboOf(modsKey, converted));
    return rawKey; // dry walk: the output is discarded
  });
  // displaced combos claim their overflow slot as they land, so a second colliding incoming
  // bind cannot stack onto the same combo (it keeps Enter, the fail-open shape)
  const displaced = new Set<string>();
  return rewriteValues(seedText, (rawKey, { section, modsKey }) => {
    const converted = convertKey(rawKey, layout);
    const to = overflow[converted];
    if (to == null || section !== "hotkeys") return converted;
    if (rawKey.trim() === converted) return converted; // the native claimant keeps its key
    if (!native.has(comboOf(modsKey, converted))) return converted; // no collision to resolve
    const target = comboOf(modsKey, to);
    if (occupied.has(target) || displaced.has(target)) return converted; // fail open: taken
    displaced.add(target);
    return to;
  });
}
